import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
    Button,
    Input,
    Label,
    ListBox,
    Select,
    Switch,
    TextField,
} from '@heroui/react';
import { Widget } from '@heroui-pro/react';
import { PATH } from '@/router/PATH';
import { DatePickerField } from '@/components/datePickerField';
import { getNodeTestIdProp } from '@/utils';
import {
    TYPE_LABEL_KEY,
    TYPE_OPTIONS,
    UNIT_LABEL_KEY,
    UNIT_OPTIONS,
    VAT_LABEL_KEY,
    VAT_OPTIONS,
    type BatchDraft,
    type VariantDraft,
} from './createProductMeta';
import type { useCreateProductForm } from './useCreateProductForm';

type CreateProductForm = ReturnType<typeof useCreateProductForm>;

type CreateProductViewProps = {
    form: CreateProductForm;
};

const NONE_VALUE = '__none__';

export const CreateProductView: React.FC<CreateProductViewProps> = ({
    form,
}) => {
    const { t } = useTranslation('pages');
    const { state } = form;

    return (
        <div
            className="flex flex-col gap-6"
            {...getNodeTestIdProp('create-product')}
        >
            {/* No page heading or back link here — the layout navbar owns both
                for this sub-page (`SUBPAGE_NAVIGATION` in layout/navigation.ts). */}
            <IdentitySection form={form} />

            {state.type === 'Variable' ? <AxesSection form={form} /> : null}

            <VariantsSection form={form} />

            {form.submitError ? (
                <p
                    className="text-sm text-danger"
                    {...getNodeTestIdProp('create-product-error')}
                >
                    {form.submitError.kind === 'i18n'
                        ? t(form.submitError.key)
                        : form.submitError.message}
                </p>
            ) : null}

            <div className="flex items-center justify-end gap-3">
                <Link
                    className="rounded-lg border border-border px-4 py-2 text-sm text-muted hover:text-foreground"
                    to={PATH.PRODUCTS.ROOT}
                    {...getNodeTestIdProp('create-product-cancel')}
                >
                    {t('products.create.cancel')}
                </Link>
                <Button
                    isDisabled={!form.canSubmit}
                    isPending={form.isSubmitting}
                    onPress={form.onSubmit}
                    variant="primary"
                    {...getNodeTestIdProp('create-product-submit')}
                >
                    {t('products.create.submit')}
                </Button>
            </div>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Identity                                                                   */
/* -------------------------------------------------------------------------- */

const IdentitySection: React.FC<CreateProductViewProps> = ({ form }) => {
    const { t } = useTranslation('pages');
    const { state } = form;

    return (
        <Widget
            className="flex flex-col gap-4 p-5"
            {...getNodeTestIdProp('create-product-identity')}
        >
            <h2 className="text-sm font-semibold text-foreground">
                {t('products.create.sections.identity')}
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <LabeledSelect
                    label={t('products.create.fields.type')}
                    onChange={(value) =>
                        form.onTypeChange(
                            value as (typeof TYPE_OPTIONS)[number],
                        )
                    }
                    options={TYPE_OPTIONS.map((option) => ({
                        id: option,
                        name: t(TYPE_LABEL_KEY[option]),
                    }))}
                    slot="type"
                    value={state.type}
                />
                <LabeledSelect
                    label={t('products.create.fields.category')}
                    onChange={(value) =>
                        form.onCategoryChange(
                            value === NONE_VALUE ? null : value,
                        )
                    }
                    options={form.categoryOptions.map((option) => ({
                        id: option.id,
                        name: option.name,
                        depth: option.depth,
                    }))}
                    placeholder={t(
                        'products.create.fields.categoryPlaceholder',
                    )}
                    slot="category"
                    value={state.categoryId ?? NONE_VALUE}
                />
                <LabeledInput
                    label={t('products.create.fields.name')}
                    onChange={form.onNameChange}
                    placeholder={t('products.create.fields.namePlaceholder')}
                    slot="name"
                    value={state.name}
                />
                <LabeledInput
                    label={t('products.create.fields.sku')}
                    onChange={form.onSkuChange}
                    placeholder={t('products.create.fields.skuPlaceholder')}
                    slot="sku"
                    value={state.sku}
                />
                <LabeledSelect
                    label={t('products.create.fields.unit')}
                    onChange={(value) =>
                        form.onUnitChange(
                            value as (typeof UNIT_OPTIONS)[number],
                        )
                    }
                    options={UNIT_OPTIONS.map((option) => ({
                        id: option,
                        name: t(UNIT_LABEL_KEY[option]),
                    }))}
                    slot="unit"
                    value={state.unit}
                />
                <LabeledSelect
                    label={t('products.create.fields.vat')}
                    onChange={(value) =>
                        form.onVatChange(value as (typeof VAT_OPTIONS)[number])
                    }
                    options={VAT_OPTIONS.map((option) => ({
                        id: option,
                        name: t(VAT_LABEL_KEY[option]),
                    }))}
                    slot="vat"
                    value={state.vatType}
                />
                <LabeledSelect
                    includeNone={t('products.create.fields.brandNone')}
                    label={t('products.create.fields.brand')}
                    onChange={(value) =>
                        form.onBrandChange(value === NONE_VALUE ? null : value)
                    }
                    options={form.brandOptions}
                    slot="brand"
                    value={state.brandId ?? NONE_VALUE}
                />
            </div>
        </Widget>
    );
};

/* -------------------------------------------------------------------------- */
/* Variation axes (Variable only)                                            */
/* -------------------------------------------------------------------------- */

const AxesSection: React.FC<CreateProductViewProps> = ({ form }) => {
    const { t } = useTranslation('pages');
    const { state, availableAxes } = form;

    return (
        <Widget
            className="flex flex-col gap-4 p-5"
            {...getNodeTestIdProp('create-product-axes')}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('products.create.sections.axes')}
                </h2>
                <Button
                    isDisabled={state.axes.length === 0}
                    onPress={form.onGenerateMatrix}
                    size="sm"
                    variant="secondary"
                    {...getNodeTestIdProp('create-product-generate-matrix')}
                >
                    <Icon icon="gravity-ui:layout-cells" />
                    {t('products.create.axes.generate')}
                </Button>
            </div>

            {state.categoryId === null ? (
                <p className="text-sm text-muted">
                    {t('products.create.axes.pickCategory')}
                </p>
            ) : availableAxes.length === 0 ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('create-product-axes-empty')}
                >
                    {t('products.create.axes.none')}
                </p>
            ) : (
                <div className="flex flex-col gap-4">
                    {availableAxes.map((axis) => {
                        const selectedAxis = state.axes.find(
                            (entry) =>
                                entry.attributeTypeId === axis.attributeTypeId,
                        );
                        return (
                            <div
                                className="flex flex-col gap-2"
                                key={axis.attributeTypeId}
                                {...getNodeTestIdProp(
                                    `create-product-axis-${axis.attributeTypeId}`,
                                )}
                            >
                                <Switch
                                    isSelected={Boolean(selectedAxis)}
                                    onChange={() =>
                                        form.onToggleAxis(axis.attributeTypeId)
                                    }
                                    {...getNodeTestIdProp(
                                        `create-product-axis-toggle-${axis.attributeTypeId}`,
                                    )}
                                >
                                    <Switch.Control>
                                        <Switch.Thumb />
                                    </Switch.Control>
                                    <Switch.Content>
                                        <Label className="text-sm font-medium">
                                            {axis.name}
                                        </Label>
                                    </Switch.Content>
                                </Switch>
                                {selectedAxis ? (
                                    <div className="flex flex-wrap gap-2 pl-10">
                                        {axis.values.map((value) => {
                                            const active =
                                                selectedAxis.valueIds.includes(
                                                    value.id,
                                                );
                                            return (
                                                <button
                                                    className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                                                        active
                                                            ? 'border-primary bg-primary/10 text-primary'
                                                            : 'border-border text-muted hover:text-foreground'
                                                    }`}
                                                    key={value.id}
                                                    onClick={() =>
                                                        form.onToggleAxisValue(
                                                            axis.attributeTypeId,
                                                            value.id,
                                                        )
                                                    }
                                                    type="button"
                                                    {...getNodeTestIdProp(
                                                        `create-product-axis-value-${value.id}`,
                                                    )}
                                                >
                                                    {value.name}
                                                </button>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </Widget>
    );
};

/* -------------------------------------------------------------------------- */
/* Variants + batches                                                        */
/* -------------------------------------------------------------------------- */

const VariantsSection: React.FC<CreateProductViewProps> = ({ form }) => {
    const { t } = useTranslation('pages');
    const { state } = form;

    return (
        <section
            className="flex flex-col gap-3"
            {...getNodeTestIdProp('create-product-variants')}
        >
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-sm font-semibold text-foreground">
                    {t('products.create.sections.variants')} (
                    {state.variants.length})
                </h2>
                {/* Simple products are exactly one variant — only Variable
                    products can add more rows. */}
                {state.type === 'Variable' ? (
                    <Button
                        onPress={form.onAddVariant}
                        size="sm"
                        variant="secondary"
                        {...getNodeTestIdProp('create-product-add-variant')}
                    >
                        <Icon icon="gravity-ui:plus" />
                        {t('products.create.variants.add')}
                    </Button>
                ) : null}
            </div>

            {state.variants.length === 0 ? (
                <p
                    className="text-sm text-muted"
                    {...getNodeTestIdProp('create-product-variants-empty')}
                >
                    {t('products.create.variants.empty')}
                </p>
            ) : (
                state.variants.map((variant, index) => (
                    <VariantCard
                        form={form}
                        index={index}
                        key={variant.key}
                        variant={variant}
                    />
                ))
            )}
        </section>
    );
};

type VariantCardProps = {
    form: CreateProductForm;
    variant: VariantDraft;
    index: number;
};

const VariantCard: React.FC<VariantCardProps> = ({ form, variant, index }) => {
    const { t } = useTranslation('pages');
    const { state, availableAxes } = form;

    return (
        <Widget
            className="flex flex-col gap-4 p-4"
            {...getNodeTestIdProp(`create-product-variant-${variant.key}`)}
        >
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">
                    {t('products.create.variants.label', { index: index + 1 })}
                </h3>
                {/* Simple keeps its single variant — only Variable rows are
                    removable. */}
                {state.type === 'Variable' ? (
                    <Button
                        isIconOnly
                        onPress={() => form.onRemoveVariant(variant.key)}
                        size="sm"
                        variant="tertiary"
                        {...getNodeTestIdProp(
                            `create-product-remove-variant-${variant.key}`,
                        )}
                    >
                        <Icon icon="gravity-ui:trash-bin" />
                    </Button>
                ) : null}
            </div>

            {form.variantErrors[variant.key] ? (
                <p
                    className="text-sm text-danger"
                    role="alert"
                    {...getNodeTestIdProp(
                        `create-product-variant-error-${variant.key}`,
                    )}
                >
                    {t(form.variantErrors[variant.key]!)}
                </p>
            ) : null}

            {state.type === 'Variable' && state.axes.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {state.axes.map((axis) => {
                        const meta = availableAxes.find(
                            (entry) =>
                                entry.attributeTypeId === axis.attributeTypeId,
                        );
                        if (!meta) {
                            return null;
                        }
                        return (
                            <LabeledSelect
                                key={axis.attributeTypeId}
                                label={meta.name}
                                onChange={(value) =>
                                    form.onVariantOptionChange(
                                        variant.key,
                                        axis.attributeTypeId,
                                        value,
                                    )
                                }
                                options={meta.values}
                                placeholder={t(
                                    'products.create.variants.optionPlaceholder',
                                )}
                                slot={`variant-${variant.key}-option-${axis.attributeTypeId}`}
                                value={
                                    variant.optionValues[
                                        axis.attributeTypeId
                                    ] ?? ''
                                }
                            />
                        );
                    })}
                </div>
            ) : null}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <LabeledInput
                    label={t('products.create.variants.barcode')}
                    onChange={(value) =>
                        form.onVariantFieldChange(variant.key, 'barcode', value)
                    }
                    slot={`variant-${variant.key}-barcode`}
                    value={variant.barcode}
                />
                <LabeledInput
                    label={t('products.create.variants.salePrice')}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.key,
                            'salePrice',
                            value,
                        )
                    }
                    slot={`variant-${variant.key}-salePrice`}
                    type="number"
                    value={variant.salePrice}
                />
                <LabeledInput
                    label={t('products.create.variants.minimum')}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.key,
                            'minimumQuantity',
                            value,
                        )
                    }
                    slot={`variant-${variant.key}-minimum`}
                    type="number"
                    value={variant.minimumQuantity}
                />
                <LabeledInput
                    label={t('products.create.variants.wholesalePrice')}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.key,
                            'wholesalePrice',
                            value,
                        )
                    }
                    slot={`variant-${variant.key}-wholesalePrice`}
                    type="number"
                    value={variant.wholesalePrice}
                />
                <DatePickerField
                    id={`create-product-input-variant-${variant.key}-saleEndDate`}
                    label={t('products.create.variants.saleEndDate')}
                    name={`variant-${variant.key}-saleEndDate`}
                    onChange={(value) =>
                        form.onVariantFieldChange(
                            variant.key,
                            'saleEndDate',
                            value,
                        )
                    }
                    value={variant.saleEndDate}
                />
                <LabeledInput
                    label={t('products.create.variants.weight')}
                    onChange={(value) =>
                        form.onVariantFieldChange(variant.key, 'weight', value)
                    }
                    slot={`variant-${variant.key}-weight`}
                    type="number"
                    value={variant.weight}
                />
                <LabeledInput
                    label={t('products.create.variants.volume')}
                    onChange={(value) =>
                        form.onVariantFieldChange(variant.key, 'volume', value)
                    }
                    slot={`variant-${variant.key}-volume`}
                    type="number"
                    value={variant.volume}
                />
            </div>

            <div className="flex flex-col gap-3 border-t border-separator pt-3">
                <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-foreground">
                        {t('products.create.batches.title')} (
                        {variant.batches.length})
                    </h4>
                    <Button
                        onPress={() => form.onAddBatch(variant.key)}
                        size="sm"
                        variant="tertiary"
                        {...getNodeTestIdProp(
                            `create-product-add-batch-${variant.key}`,
                        )}
                    >
                        <Icon icon="gravity-ui:plus" />
                        {t('products.create.batches.add')}
                    </Button>
                </div>
                {variant.batches.length === 0 ? (
                    <p className="text-xs text-muted">
                        {t('products.create.batches.empty')}
                    </p>
                ) : (
                    variant.batches.map((batch) => (
                        <BatchRow
                            batch={batch}
                            form={form}
                            key={batch.key}
                            variantKey={variant.key}
                        />
                    ))
                )}
            </div>
        </Widget>
    );
};

type BatchRowProps = {
    form: CreateProductForm;
    variantKey: string;
    batch: BatchDraft;
};

const BatchRow: React.FC<BatchRowProps> = ({ form, variantKey, batch }) => {
    const { t } = useTranslation('pages');

    return (
        <div
            className="grid grid-cols-1 items-end gap-3 rounded-lg bg-surface-secondary p-3 sm:grid-cols-3 lg:grid-cols-7"
            {...getNodeTestIdProp(`create-product-batch-${batch.key}`)}
        >
            <LabeledInput
                label={t('products.create.batches.number')}
                onChange={(value) =>
                    form.onBatchFieldChange(
                        variantKey,
                        batch.key,
                        'batchNumber',
                        value,
                    )
                }
                slot={`batch-${batch.key}-number`}
                value={batch.batchNumber}
            />
            <LabeledInput
                label={t('products.create.batches.quantity')}
                onChange={(value) =>
                    form.onBatchFieldChange(
                        variantKey,
                        batch.key,
                        'quantity',
                        value,
                    )
                }
                slot={`batch-${batch.key}-quantity`}
                type="number"
                value={batch.quantity}
            />
            <LabeledInput
                label={t('products.create.batches.purchasePrice')}
                onChange={(value) =>
                    form.onBatchFieldChange(
                        variantKey,
                        batch.key,
                        'purchasePrice',
                        value,
                    )
                }
                slot={`batch-${batch.key}-purchasePrice`}
                type="number"
                value={batch.purchasePrice}
            />
            <LabeledSelect
                includeNone={t('products.create.batches.supplierNone')}
                label={t('products.create.batches.supplier')}
                onChange={(value) =>
                    form.onBatchSupplierChange(
                        variantKey,
                        batch.key,
                        value === NONE_VALUE ? null : value,
                    )
                }
                options={form.supplierOptions}
                slot={`batch-${batch.key}-supplier`}
                value={batch.supplierId ?? NONE_VALUE}
            />
            <LabeledSelect
                label={t('products.create.batches.branch')}
                onChange={(value) =>
                    form.onBatchBranchChange(
                        variantKey,
                        batch.key,
                        value === NONE_VALUE ? null : value,
                    )
                }
                options={form.branchOptions}
                slot={`batch-${batch.key}-branch`}
                value={batch.branchId ?? NONE_VALUE}
            />
            <DatePickerField
                id={`create-product-input-batch-${batch.key}-received`}
                label={t('products.create.batches.received')}
                name={`batch-${batch.key}-received`}
                onChange={(value) =>
                    form.onBatchFieldChange(
                        variantKey,
                        batch.key,
                        'receivedDate',
                        value,
                    )
                }
                value={batch.receivedDate}
            />
            <DatePickerField
                id={`create-product-input-batch-${batch.key}-expiry`}
                label={t('products.create.batches.expiry')}
                name={`batch-${batch.key}-expiry`}
                onChange={(value) =>
                    form.onBatchFieldChange(
                        variantKey,
                        batch.key,
                        'expiryDate',
                        value,
                    )
                }
                value={batch.expiryDate}
            />
            <Button
                isIconOnly
                onPress={() => form.onRemoveBatch(variantKey, batch.key)}
                size="sm"
                variant="tertiary"
                {...getNodeTestIdProp(
                    `create-product-remove-batch-${batch.key}`,
                )}
            >
                <Icon icon="gravity-ui:trash-bin" />
            </Button>
        </div>
    );
};

/* -------------------------------------------------------------------------- */
/* Render-only field primitives                                              */
/* -------------------------------------------------------------------------- */

type LabeledInputProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    slot: string;
    placeholder?: string;
    type?: 'text' | 'number';
};

/**
 * A HeroUI `TextField` with a real `Label` (BOS-170).
 *
 * This was a hand-rolled `<input>` under a `text-xs text-muted` span, which is
 * the same treatment this file gives placeholder text — so the variant fields
 * read as a column of grey hints with nothing naming them, and the reader had
 * to guess which box was the barcode. A `Label` is the element that names a
 * field, and it looks like one because HeroUI styles it as one.
 */
const LabeledInput: React.FC<LabeledInputProps> = ({
    label,
    value,
    onChange,
    slot,
    placeholder,
    type = 'text',
}) => (
    <TextField
        onChange={onChange}
        type={type}
        value={value}
        {...getNodeTestIdProp(`create-product-input-${slot}`)}
    >
        <Label>{label}</Label>
        <Input placeholder={placeholder} />
    </TextField>
);

type SelectOption = { id: string; name: string; depth?: number };

type LabeledSelectProps = {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    slot: string;
    placeholder?: string;
    includeNone?: string;
};

const LabeledSelect: React.FC<LabeledSelectProps> = ({
    label,
    value,
    onChange,
    options,
    slot,
    placeholder,
    includeNone,
}) => (
    <Select
        fullWidth
        onChange={(next) => onChange(String(next))}
        placeholder={placeholder}
        value={value}
        variant="secondary"
    >
        <Label className="text-xs text-muted">{label}</Label>
        <Select.Trigger {...getNodeTestIdProp(`create-product-select-${slot}`)}>
            <Select.Value />
            <Select.Indicator />
        </Select.Trigger>
        <Select.Popover>
            <ListBox>
                {includeNone ? (
                    <ListBox.Item id={NONE_VALUE} textValue={includeNone}>
                        {includeNone}
                        <ListBox.ItemIndicator />
                    </ListBox.Item>
                ) : null}
                {options.map((option) => (
                    <ListBox.Item
                        id={option.id}
                        key={option.id}
                        textValue={option.name}
                    >
                        <span
                            style={{
                                paddingInlineStart: option.depth
                                    ? `${option.depth * 12}px`
                                    : undefined,
                            }}
                        >
                            {option.name}
                        </span>
                        <ListBox.ItemIndicator />
                    </ListBox.Item>
                ))}
            </ListBox>
        </Select.Popover>
    </Select>
);
